// deps
import React, { ReactElement, memo, RefObject, useContext, useMemo, useCallback } from "react";

// internal-deps
import { RenderingDetails, PresentationInfo, ComparisonNudgeVariant } from "@swiggy-private/cart-types";
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// components
import { TGWrapperProps } from "@cart-client/components/swiggy-plan-bulls-eye-tg-wrapper";
import { DeliverySectionRenderer } from "@cart-client/components/delivery-section-renderer";
import { DeliverySectionContextProvider } from "@cart-client/components/delivery-section-context-provider/DeliverySectionContextProvider";
import { CartOtherItemsRenderer } from "@cart-client/components/cart-other-items-renderer";
import { BillDetailsRenderer } from "@cart-client/components/bill-details-renderer";
import { PLBillDetailsRenderer } from "@cart-client/components/pl-bill-details-renderer";
import { SavingsSectionRenderer } from "@cart-client/components/savings-section-renderer";
import { CartMenuItemsAndSuggestions } from "@cart-client/components/cart-menu-items-and-suggestions";
import { ErrorBoundary } from "@swiggy-private/core-ui";

import { recordPLBillDetailsError } from "@cart-client/components/pl-bill-details-renderer/PLBillDetailsNREvents";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// hooks
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";

// helpers
import {
    hideWidgetsBasedOnConfig,
    isToingDeliveryModesXp,
    reorderItemsForCouponXP,
    transformRenderingItems,
} from "@cart-client/helpers/CartRendering";
import { getSavingsBannerData, getIrctcIdentifier } from "@cart-client/helpers/Cart";
import { getNestedValue } from "@swiggy-private/js-utils";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { getSubscriptionRenewalWidgetPositioning } from "@cart-client/helpers/SubscriptionRenewalWidget";
import { getPLBillDetails } from "@cart-client/helpers/PLBillDetials";

// constants
import {
    SCROLL_MARGIN_TOP_WITH_OFO_BANNER,
    SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
    SCROLL_MARGIN_TOP_WITHOUT_BANNER,
} from "@cart-client/components/cart-renderer/constants";
import useCrazyDealsExpiryCrouton from "@cart-client/hooks/useCrazyDealsExpiryCrouton/useCrazyDealsExpiryCrouton";
import SubscriptionRenewalWidget from "@cart-client/components/subscription-renewal-widget/SubscriptionRenewalWidget";

// styles
import { SavingsSectionWrapper } from "./styles";

export interface IProps {
    canShowTimer: boolean;
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    canShowOFOWidgetAtTop?: boolean;
    planBullsEyeTGProps?: TGWrapperProps;
    planAddedRef: RefObject<HTMLDivElement>;
    goToMenu: (restaurantId?: string | number) => void;
    isCroutonVisible: boolean;
    isValueMeal: boolean;
    hideSavingsSectionTitle?: boolean;
}

function CartWidgetRenderer({
    canShowTimer,
    canShowOFOWidgetAtTop,
    setCroutonVisibility,
    planAddedRef,
    planBullsEyeTGProps,
    goToMenu,
    isCroutonVisible,
    isValueMeal,
    hideSavingsSectionTitle = false,
}: IProps): ReactElement {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const {
        data: { cartSource },
    } = useContext(CartFlowContext);

    const { couponOrderXP } = useCartRendererXP(cartResponse);

    useCrazyDealsExpiryCrouton({ setCroutonVisibility, canShowTimer });

    const renderingDetailsFromServer = useMemo(
        () => cartResponse?.data?.rendering_details ?? [],
        [cartResponse?.data?.rendering_details],
    );

    const renderingDetails: RenderingDetails[] = useMemo(
        () =>
            hideWidgetsBasedOnConfig(
                reorderItemsForCouponXP(renderingDetailsFromServer, couponOrderXP),
                cartResponse?.data?.widget_info,
            ),
        [renderingDetailsFromServer, couponOrderXP, cartResponse?.data?.widget_info],
    );

    const _renderingDetails: RenderingDetails[] = useMemo(
        () => transformRenderingItems(renderingDetails),
        [renderingDetails],
    );

    const swiggyPlanInfo: PresentationInfo = useMemo(
        () => getNestedValue(cartResponse, "data.swiggyOneInfo.presentationInfo"),
        [cartResponse],
    );

    const subscriptionRenewalWidgetPositioning = useMemo(
        () => getSubscriptionRenewalWidgetPositioning(swiggyPlanInfo),
        [swiggyPlanInfo],
    );

    const planDetails = useMemo(() => swiggyPlanInfo?.plan_details, [swiggyPlanInfo?.plan_details]);

    const statusBarHeight = GlobalHeaderState.getHeader(SwgyHeaderKeysList.statusBarHeight) || 0;
    const savingsBannerData = useMemo(() => getSavingsBannerData(cartResponse), [cartResponse]);

    const cartTopOffset = useMemo(() => {
        let topOffsetValue = Number(statusBarHeight);

        if (canShowOFOWidgetAtTop) {
            topOffsetValue += SCROLL_MARGIN_TOP_WITH_OFO_BANNER;
        } else if (!isEmpty(savingsBannerData)) {
            topOffsetValue += SCROLL_MARGIN_TOP_WITH_SAVING_BANNER;
        } else {
            topOffsetValue += SCROLL_MARGIN_TOP_WITHOUT_BANNER;
        }
        return topOffsetValue;
    }, [canShowOFOWidgetAtTop, savingsBannerData, statusBarHeight]);

    const isIrctcCartSession = useMemo(() => getIrctcIdentifier(cartResponse), [cartResponse]);

    const toingDeliveryModesXp = useMemo(() => isToingDeliveryModesXp(cartResponse), [cartResponse]);

    const plBillDetails = useMemo(() => getPLBillDetails(cartResponse?.data), [cartResponse?.data]);

    const renderRemainingCartWidgetsV2 = useCallback(() => {
        const savingsSection = (
            <SavingsSectionWrapper>
                <SavingsSectionRenderer
                    renderingDetails={_renderingDetails}
                    hideSectionTitle={hideSavingsSectionTitle}
                />
            </SavingsSectionWrapper>
        );
        const deliverySection = !isIrctcCartSession ? (
            <DeliverySectionRenderer
                setCroutonVisibility={setCroutonVisibility}
                renderingDetails={_renderingDetails}
                cartTopOffset={cartTopOffset}
            />
        ) : null;

        return (
            <>
                {toingDeliveryModesXp ? (
                    <>
                        {deliverySection}
                        {savingsSection}
                    </>
                ) : (
                    <>
                        {savingsSection}
                        {deliverySection}
                    </>
                )}
                <CartOtherItemsRenderer
                    renderingDetails={_renderingDetails}
                    setCroutonVisibility={setCroutonVisibility}
                />
                {subscriptionRenewalWidgetPositioning === ComparisonNudgeVariant.VARIANT_BOTTOM && (
                    <SubscriptionRenewalWidget />
                )}
                {!isEmpty(plBillDetails) ? (
                    <ErrorBoundary
                        errorContent={
                            <BillDetailsRenderer
                                renderingDetails={_renderingDetails}
                                planAddedRef={planAddedRef}
                                setBillDetailsOneCardComms={planBullsEyeTGProps?.setBillDetailsOneCardComms}
                                planDetails={planDetails}
                            />
                        }
                        errorFallback={recordPLBillDetailsError}
                    >
                        <PLBillDetailsRenderer
                            details={plBillDetails}
                            setBillDetailsOneCardComms={planBullsEyeTGProps?.setBillDetailsOneCardComms}
                            planDetails={planDetails}
                        />
                    </ErrorBoundary>
                ) : (
                    <BillDetailsRenderer
                        renderingDetails={_renderingDetails}
                        planAddedRef={planAddedRef}
                        setBillDetailsOneCardComms={planBullsEyeTGProps?.setBillDetailsOneCardComms}
                        planDetails={planDetails}
                    />
                )}
            </>
        );
    }, [
        _renderingDetails,
        hideSavingsSectionTitle,
        isIrctcCartSession,
        cartTopOffset,
        plBillDetails,
        planAddedRef,
        planBullsEyeTGProps?.setBillDetailsOneCardComms,
        planDetails,
        setCroutonVisibility,
        subscriptionRenewalWidgetPositioning,
        toingDeliveryModesXp,
    ]);

    return (
        <>
            {subscriptionRenewalWidgetPositioning === ComparisonNudgeVariant.VARIANT_TOP && (
                <SubscriptionRenewalWidget isTopPositioned={true} />
            )}
            {/* We don't need this for Gifting Cart as it has its own items rendrer and does not need cross sell rendrer */}
            {!isGiftingCartSource(cartSource) && (
                <CartMenuItemsAndSuggestions
                    isValueMeal={isValueMeal}
                    isCroutonVisible={isCroutonVisible}
                    renderingDetails={_renderingDetails}
                    goToMenu={goToMenu}
                />
            )}
            <DeliverySectionContextProvider canShowTimer={canShowTimer}>
                {renderRemainingCartWidgetsV2()}
            </DeliverySectionContextProvider>
        </>
    );
}

export default memo(CartWidgetRenderer);
